import { Component, effect, inject, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { CompanyView, OpportunityCompanyView, SearchCompaniesForOpportunityParameters } from '@api-proxies';
import { IKebabAction } from '@app/components/shared/grids/custom-cell-actions/custom-cell-actions.component';
import { TypeAheadCompaniesComponent } from '@app/components/shared/type-ahead-companies/type-ahead-companies.component';
import { GridService } from '@app/services/grid.service';
import { OpportunityCompanyService } from '@http/clients/opportunity-company.service';
import { OpportunityService } from '@http/clients/opportunity.service';
import { ITypeAheadDropdownEntityParameters } from '@interfaces/type-ahead-dropdown-entity-parameters.interface';
import { EntityTypes } from '@models/enums/EntityTypes';
import { OpportunityAction } from '@models/enums/OpportunityAction';
import { AwaiterService } from '@services/awaiter.service';
import { ServerSideGridComponent } from '@shared/grids/server-side-grid/server-side-grid.component';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { ModalEditOpportunityCompanyComponent } from '@shared/modals/modal-edit-opportunity-company/modal-edit-opportunity-company.component';
import { ColDef, IServerSideDatasource } from 'ag-grid-enterprise';
import { ModalService, SnackbarNotificationService } from 'ngx-lib';

import { OPPORTUNITY_COMPANIES_GRID_COLUMNS } from './opportunities-single-companies.config';

@Component({
    selector: 'app-opportunities-single-companies',
    templateUrl: './opportunities-single-companies.component.html',
    styleUrls: ['./opportunities-single-companies.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ServerSideGridComponent, TypeAheadCompaniesComponent]
})
export class OpportunitiesSingleCompaniesComponent {
    companiesDatasource?: IServerSideDatasource;
    noResults = signal<boolean>(false);
    isLoading = signal<boolean>(false);
    isActionAllowed = signal<boolean>(false);
    gridColumns: ColDef[] = [...OPPORTUNITY_COMPANIES_GRID_COLUMNS];

    typeAheadSearchByEntityParameters: ITypeAheadDropdownEntityParameters = {
        entityKeyName: EntityTypes.Opportunity
    };

    opportunityId?: number;
    listEntitiesUpdated = output<void>();

    private readonly opportunityCompanyService = inject(OpportunityCompanyService);
    private readonly opportunityService = inject(OpportunityService);
    private readonly awaiter = inject(AwaiterService);
    private readonly modalService = inject(ModalService);
    private readonly snackbarNotificationService = inject(SnackbarNotificationService);
    private readonly gridService = inject(GridService);
    private availableActions: number[] = [];

    constructor() {
        this.opportunityId = this.opportunityService.opportunity()?.id;

        effect(() => {
            const opportunity = this.opportunityService.opportunity();
            if (opportunity?.id) {
                this.opportunityId = opportunity.id;
                this.availableActions = this.opportunityService.availableActions();
                this.isActionAllowed.set(this.availableActions.includes(OpportunityAction.EditContact));
            }
            if (this.opportunityId && !this.companiesDatasource) {
                this.initializeComponent();
            }
        });
    }

    getRowIdFn = (params: any) => params.data.id.toString();

    async initializeComponent(): Promise<void> {
        if (!this.opportunityId) return;

        this.isActionAllowed.set(this.availableActions.includes(OpportunityAction.EditCompany));
        this.setupGridColumns();

        this.typeAheadSearchByEntityParameters.entity = {
            opportunityId: this.opportunityId
        };

        this.createDatasource();
    }

    async onSelectedCompany(company: CompanyView): Promise<void> {
        if (!company || !this.opportunityId) return;

        try {
            const companySaved = await this.opportunityService.saveCompany({
                companyId: company.id,
                opportunityId: this.opportunityId
            } as any as OpportunityCompanyView);

            if (companySaved) {
                const gridApi = this.gridService.getGridApi();

                let updated = false;

                if (gridApi?.applyServerSideTransaction) {
                    const transactionResult = gridApi.applyServerSideTransaction({
                        add: [companySaved]
                    });

                    updated = transactionResult?.status === 'Applied';
                }

                if (!updated) {
                    this.refreshGridView();
                }

                this.noResults.set(false);
                this.updateOrAddCompanyInService(companySaved);
                this.listEntitiesUpdated.emit();
            }
        } catch {
            this.snackbarNotificationService.error('An error occurred while trying to save the company');
        }
    }

    async editItem(opportunityCompany: OpportunityCompanyView): Promise<void> {
        const opportunityCompanyUpdated = await this.modalService.open(ModalEditOpportunityCompanyComponent, {
            company: opportunityCompany
        });

        if (opportunityCompanyUpdated) {
            const gridApi = this.gridService.getGridApi();
            let updated = false;

            if (gridApi) {
                const node = gridApi.getRowNode(opportunityCompanyUpdated.id.toString());

                if (node?.data && gridApi.applyServerSideTransaction) {
                    const transactionResult = gridApi.applyServerSideTransaction({
                        update: [opportunityCompanyUpdated]
                    });
                    updated = transactionResult?.status === 'Applied';
                }
            }

            if (!updated) {
                this.refreshGridView();
            }

            this.updateOrAddCompanyInService(opportunityCompanyUpdated);
        }
    }

    async removeItem(opportunityCompany: OpportunityCompanyView): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Remove company',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to remove this company from the company list?',
            title: 'Remove company'
        });

        if (!responseOk) return;

        const { id: opportunityCompanyId } = opportunityCompany;
        if (!opportunityCompanyId) return;

        await this.awaiter.awaitAction(
            'Removing Company',
            async () => {
                await this.opportunityCompanyService.delete(opportunityCompanyId);

                const gridApi = this.gridService.getGridApi();
                let removed = false;

                if (gridApi) {
                    const node = gridApi.getRowNode(opportunityCompanyId.toString());

                    if (node?.data && gridApi.applyServerSideTransaction) {
                        const transactionResult = gridApi.applyServerSideTransaction({
                            remove: [node.data]
                        });
                        removed = transactionResult?.status === 'Applied';
                    }

                    if (removed) {
                        this.noResults.set(gridApi.getDisplayedRowCount() === 0);
                    }
                }

                if (!removed) {
                    this.refreshGridView();
                }

                this.removeCompanyFromService(opportunityCompanyId);
                this.listEntitiesUpdated.emit();
            },
            (loading) => this.isLoading.set(loading)
        );
    }

    async refreshGridView() {
        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        gridApi.refreshServerSide({ purge: true });
    }

    private setupGridColumns(): void {
        const actions: IKebabAction[] = [
            {
                label: 'Edit',
                action: (company: OpportunityCompanyView) => this.editItem(company),
                show: () => this.isActionAllowed()
            },
            {
                label: 'Remove',
                action: (company: OpportunityCompanyView) => this.removeItem(company),
                show: () => this.isActionAllowed()
            }
        ];

        const baseColumns = this.isActionAllowed()
            ? OPPORTUNITY_COMPANIES_GRID_COLUMNS
            : OPPORTUNITY_COMPANIES_GRID_COLUMNS.filter((col) => col.colId !== 'actions');

        this.gridColumns = baseColumns.map((col) => {
            if (col.colId === 'actions') {
                return {
                    ...col,
                    cellRendererParams: {
                        actions
                    }
                };
            }
            return col;
        });
    }

    private searchParamsBuilder(gridParams: any): SearchCompaniesForOpportunityParameters {
        const { startRow, sortModel } = gridParams;
        const pageSize = 20;
        const pageNumber = Math.floor(startRow / pageSize) + 1;
        const opportunityId = this.opportunityId ?? 0;

        const searchParams = {
            pageNumber,
            pageSize,
            ...(sortModel?.length && sortModel[0].sort
                ? {
                      sortBy: sortModel[0].colId,
                      sortDirection: sortModel[0].sort === 'asc' ? 'A' : 'D'
                  }
                : {}),
            opportunityId
        } as SearchCompaniesForOpportunityParameters;

        return searchParams;
    }

    private createDatasource(): void {
        this.companiesDatasource = {
            getRows: async (params: any) => {
                try {
                    this.isLoading.set(true);
                    this.noResults.set(false);

                    const searchParams = this.searchParamsBuilder(params.request);

                    const response = await this.opportunityCompanyService.search(searchParams);

                    if (response && response.results) {
                        const rowData = response.results || [];
                        const totalCount = response.pageInfo?.itemsCount || 0;

                        this.noResults.set(rowData.length === 0 && params.request.startRow === 0);

                        params.success({
                            rowData: rowData,
                            rowCount: totalCount
                        });
                    } else {
                        console.error('Invalid response structure:', response);
                        params.fail();
                        this.noResults.set(true);
                    }
                } catch (error) {
                    console.error('Error fetching companies:', error);
                    params.fail();
                    this.noResults.set(true);
                } finally {
                    this.isLoading.set(false);
                }
            }
        };
    }

    private removeCompanyFromService(companyId: number): void {
        const currentCompanies = [...(this.opportunityService.opportunityCompanies() || [])];
        const index = currentCompanies.findIndex((c) => c.id === companyId);

        if (index !== -1) {
            currentCompanies.splice(index, 1);
            this.opportunityService.updateOpportunityCompanies(currentCompanies);
        }
    }

    private updateOrAddCompanyInService(company: OpportunityCompanyView): void {
        const currentCompanies = [...(this.opportunityService.opportunityCompanies() || [])];
        const index = currentCompanies.findIndex((c) => c.id === company.id);

        if (index !== -1) {
            currentCompanies[index] = company;
        } else {
            currentCompanies.push(company);
        }

        this.opportunityService.updateOpportunityCompanies(currentCompanies);
    }
}
